import { useEmailPreviewFrame } from "@/lib/utils/useEmailPreviewFrame";
import { IconAlertTriangle, IconQrcode } from "@tabler/icons-react";
import { replaceVariablesInHtml } from "@/lib/utils/variable-replacer";
import { expandConditionalMarkers } from "@/lib/utils/expand-conditional-markers";
import {
  expandDataTableMarkers,
  htmlHasDataTableSource,
  type RealTableRecords,
} from "@/lib/utils/expand-data-table-markers";
import { resolveQrPlaceholdersForPreview } from "@/lib/utils/qr-preview";
import { QR_GUEST_SAMPLE_VALUE } from "@/lib/utils/qr-markers";
import { ensureBuilderFonts } from "@/lib/utils/builder-fonts";
import {
  resolveLangValue,
  resolveLangKey,
  resolveLangKeyFromMap,
} from "@/lib/utils/resolve-lang";
import { useEventVariables } from "@/hooks/useEventVariables";
import { useMainGuestVariables } from "@/hooks/useMainGuestVariables";
import { getCompanions } from "@/lib/api/handlers/GuestHandling";
import { COMPANION_SCOPE } from "@/types/enums/CompanionScope";
import { getGuestAppointmentsPreview } from "@/lib/slotting/api";
import {
  buildCompanionRecords,
  COMPANION_PREVIEW_FIELDS,
} from "@/components/A4Builder/companion-record";
import { buildAppointmentRecords } from "@/components/A4Builder/appointment-record";
import { useEventTimezone } from "@/providers/EventProvider";
import { Guest } from "@/types/Guest";
import { useRef, useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslations } from "next-intl";
import { useFields } from "@/providers/FieldsProvider";

type PreviewProps = {
  data: {
    html: Record<string, string>;
    emailTemplateId: string;
    languages: string[];
    primaryLanguage: string;
    /** The template's declared primary — the fallback the worker really uses.
     *  `primaryLanguage` above is the guest-MATCHED language in the single-guest
     *  dialog (see `buildTemplateData`), so it cannot serve as the fallback. */
    truePrimaryLanguage: string;
    templateName: string;
    replyToMail: string;
    bccMail: string;
    mailSubject: Record<string, string>;
    contentAreaBackgroundColor: string;
  };
  guests: Guest[] | null;
  currentGuest: number;
  eventId?: string;
};


/**
 * Writes the preview into the sandboxed iframe, plus the builder display
 * webfont so the preview paints the same Playfair the PDF renderer embeds
 * (preview ↔ PDF font parity).
 */
function writePreviewHtml(doc: Document, html: string) {
  // document.write, not innerHTML on the root: innerHTML into the
  // about:blank document drops the builder's doctype and leaves the frame in
  // quirks mode, where html/body stretch to the viewport and every height
  // measurement equals the frame itself — it could only ever grow (W1-3).
  // The sandbox (allow-same-origin, no allow-scripts) keeps inline scripts
  // inert. Fonts AFTER the write — open() would have discarded them.
  doc.open();
  doc.write(html);
  doc.close();
  ensureBuilderFonts(doc);
}

export const Preview: React.FC<PreviewProps> = ({
  data,
  guests,
  currentGuest,
  eventId,
}) => {
  const t = useTranslations("emailPreviewComponent");
  const tSend = useTranslations("pSendEmail");
  const params = useParams();
  const resolvedEventId = eventId ?? (params?.eventId as string | undefined);
  const [previewHtml, setPreviewHtml] = useState("");
  // The token endpoint refused this guest's check-in token (403): the operator
  // may not mint it, so the body shows a mock code in the author's colours and
  // this badge says what the real send does — mint one per recipient.
  const [qrPerRecipient, setQrPerRecipient] = useState(false);
  // Desktop-width frame (>= 640px, above the mail's own mobile breakpoint —
  // 620 for a 600 px mail, its width + 20 for a wider one;
  // grows once per email to its own width) — useEmailPreviewFrame.
  const {
    containerRef,
    iframeRef,
    width: iframeWidth,
    height: iframeHeight,
    scale,
  } = useEmailPreviewFrame(previewHtml, writePreviewHtml);

  // Depend on the resolved guest OBJECT, not the `guests` array: PSendEmail
  // rebuilds that array via `.filter()` on every render (new identity), but
  // the element references are stable, so keying on `guest` avoids re-running
  // the async token fetch + QR render on every parent re-render.
  const rawGuest = guests && guests[currentGuest] ? guests[currentGuest] : null;
  // Bind the `mainGuest` source so `{{mainGuest.<field>}}` resolves like a real
  // send: uses the pre-joined `mainGuestDTO` when present, else fetches the main
  // guest once (single-guest send modal). Recipients with no main guest get a
  // blank — never a literal token.
  const guestWithMain = useMainGuestVariables(resolvedEventId, rawGuest);
  // Merge event-level vars ({{eventDate}}/{{eventTime}}/{{eventLocation}} …)
  // onto the guest so the preview resolves them like a real send does. Memoized
  // so the enriched object identity is stable across re-renders (the effect
  // below keys on it to avoid re-running the async QR fetch).
  const { enrich: enrichWithEventVars } = useEventVariables(resolvedEventId);
  const guest = useMemo(
    () => (guestWithMain ? enrichWithEventVars(guestWithMain) : null),
    [guestWithMain, enrichWithEventVars],
  );

  // Real companion rows for the recipient's data-bound `companions` DataTable,
  // tagged with the `guestId|effLang` key they were built for. `null` until
  // fetched; a non-null entry carries the recipient's actual set (the array may
  // be empty). The tag guards BOTH stale-recipient bleed AND stale-LANGUAGE
  // bleed: rows are cached FORMATTED (dates/booleans localized), and a template
  // switch inside the dialog can change the render language (effLang) for the
  // same guest — those must be a cache miss, never reused. Cached per key so
  // navigating back to a recipient is instant. Only fetched when the body
  // actually contains a DataTable marker.
  const [companionState, setCompanionState] = useState<{
    key: string;
    records: Record<string, string>[];
  } | null>(null);
  const companionCacheRef = useRef<Map<string, Record<string, string>[]>>(
    new Map(),
  );
  // guestId whose companion fetch failed: release the hold and let the preview
  // fall back to SAMPLE rows rather than blanking the email forever on a
  // transient error.
  const [companionErrorId, setCompanionErrorId] = useState<string | null>(null);
  // Real appointment rows for the recipient's data-bound `appointments`
  // DataTable, tagged + cached per `guestId|effLang` exactly like companions
  // above (stale recipient OR stale language never bleeds in; revisits paint
  // instantly). Only fetched when the body actually contains an appointments
  // table. Dates/times are FORMATTED at build (effLang); the status enum stays
  // RAW — the marker expander status-filters / sorts / humanizes it like the
  // consumer renderer.
  const [appointmentState, setAppointmentState] = useState<{
    key: string;
    records: Record<string, string>[];
  } | null>(null);
  const appointmentCacheRef = useRef<Map<string, Record<string, string>[]>>(
    new Map(),
  );
  const [appointmentErrorId, setAppointmentErrorId] = useState<string | null>(
    null,
  );
  const eventTimezone = useEventTimezone();
  const recipientGuestId = rawGuest?.guestId;
  const recipientLang = rawGuest?.language;
  // CURRENCY field defs (name → ISO 4217 code) so preview DataTable companion
  // money columns render localized (e.g. "3.519,50 €") instead of a bare number.
  const { allFieldsUnfiltered } = useFields();
  // Additional-field definitions keyed by NAME → drives type-aware rendering of
  // custom fields in both the scalar `{{additionalFields.x}}` vars and the
  // DataTable companion columns, matching the real send (localized boolean /
  // date / currency / number instead of raw ISO / true / bare number).
  const fieldsByName = useMemo(() => {
    const map = new Map<string, (typeof allFieldsUnfiltered)[number]>();
    for (const f of allFieldsUnfiltered) map.set(f.name, f);
    return map;
  }, [allFieldsUnfiltered]);
  // Resolved RENDER language (effLang) — the language the body actually renders
  // in for THIS recipient (guest.language → template primary). Mirrors the
  // consumer's `resolveLangKey`/`__renderLang`: the preview must render the
  // recipient's language, not always the primary, and typed values must format
  // in that same language.
  //
  // Map-based, and against the TRUE primary: the tag-list resolver this used to
  // call cannot see that a declared variant is empty, skips the primary's own
  // base step, and picks same-base variants in storage order — three ways to
  // preview a language the recipient will not receive.
  const effLang = useMemo(
    () =>
      resolveLangKeyFromMap(data.html, recipientLang, data.truePrimaryLanguage) ??
      recipientLang ??
      data.truePrimaryLanguage,
    [data.html, recipientLang, data.truePrimaryLanguage],
  );
  const previewCtx = useMemo(
    () => ({ fieldsByName, timeZone: eventTimezone, lang: effLang }),
    [fieldsByName, eventTimezone, effLang],
  );
  // Composite cache/tag key for formatted preview rows: the guest AND the
  // render language. effLang shifts when the admin switches templates in the
  // dialog (different language set / primary), and cached rows carry baked-in
  // localized dates — reusing them across a language change would show the
  // previous template's formatting.
  const recordsKey = recipientGuestId
    ? `${recipientGuestId}|${effLang ?? ""}`
    : undefined;
  const primaryHtml = resolveLangValue(
    data.html,
    effLang,
    data.primaryLanguage,
    "",
  );
  const hasDataTable = primaryHtml.includes("{{dataTable:");
  // Only fetch appointments when a table actually binds the appointments source
  // — an email with only companions/custom tables pays no slotting round-trip.
  const hasAppointmentTable = useMemo(
    () => htmlHasDataTableSource(primaryHtml, "appointments"),
    [primaryHtml],
  );

  useEffect(() => {
    if (!hasDataTable || !recipientGuestId || !recordsKey || !resolvedEventId) {
      setCompanionState(null);
      return;
    }
    const cached = companionCacheRef.current.get(recordsKey);
    if (cached) {
      // Synchronous set tagged to this recipient+language → revisits skip the
      // gate and paint real rows on the first frame (no blank, no flash).
      setCompanionState({ key: recordsKey, records: cached });
      return;
    }
    let cancelled = false;
    // COMPANION_SCOPE.ALL: mirror the real send (the enricher never filters
    // acceptanceState) so DECLINED/PENDING companions surface in the preview.
    // This is the one surface that must show what exists rather than who is
    // coming — a preview that hides a row the mail will print is a lie.
    getCompanions(
      resolvedEventId,
      recipientGuestId,
      COMPANION_PREVIEW_FIELDS,
      COMPANION_SCOPE.ALL,
    )
      .then((res) => {
        if (cancelled || !Array.isArray(res)) return;
        // effLang, NOT the guest's raw language: the real send formats rows
        // with the body variant's language (consumer passes effLang to the
        // enricher), so the preview must too.
        const records = buildCompanionRecords(
          res,
          effLang,
          fieldsByName,
          eventTimezone,
        );
        companionCacheRef.current.set(recordsKey, records);
        setCompanionState({ key: recordsKey, records });
      })
      .catch(() => {
        // Fetch failed: mark this recipient so the hold releases and the
        // preview falls back to SAMPLE rows instead of staying blank forever.
        if (!cancelled) setCompanionErrorId(recipientGuestId);
      });
    return () => {
      cancelled = true;
    };
  }, [hasDataTable, recipientGuestId, recordsKey, effLang, resolvedEventId, fieldsByName, eventTimezone]);

  // Real appointments fetch — mirrors the companions effect (cache per guestId,
  // tagged set, fail-soft fallback to SAMPLE). Gated on an appointments table
  // actually being present.
  useEffect(() => {
    if (!hasAppointmentTable || !recipientGuestId || !recordsKey || !resolvedEventId) {
      setAppointmentState(null);
      return;
    }
    const cached = appointmentCacheRef.current.get(recordsKey);
    if (cached) {
      setAppointmentState({ key: recordsKey, records: cached });
      return;
    }
    let cancelled = false;
    getGuestAppointmentsPreview(resolvedEventId, recipientGuestId)
      .then((res) => {
        if (cancelled || !Array.isArray(res)) return;
        // effLang, NOT the guest's raw language — see the companions effect.
        const records = buildAppointmentRecords(
          res,
          effLang,
          eventTimezone,
        );
        appointmentCacheRef.current.set(recordsKey, records);
        setAppointmentState({ key: recordsKey, records });
      })
      .catch(() => {
        if (!cancelled) setAppointmentErrorId(recipientGuestId);
      });
    return () => {
      cancelled = true;
    };
  }, [hasAppointmentTable, recipientGuestId, recordsKey, effLang, resolvedEventId, eventTimezone]);

  // Records count as "real" only when they belong to the active recipient AND
  // render language — a leftover set from the previous recipient (or a template
  // switch that changed effLang) must not bleed into this one.
  const recordsMatchRecipient =
    !!companionState && !!recordsKey && companionState.key === recordsKey;
  const appointmentsMatchRecipient =
    !!appointmentState && !!recordsKey && appointmentState.key === recordsKey;

  const realRecords = useMemo<RealTableRecords>(
    () => ({
      companions: recordsMatchRecipient ? companionState!.records : undefined,
      appointments: appointmentsMatchRecipient
        ? appointmentState!.records
        : undefined,
    }),
    [recordsMatchRecipient, companionState, appointmentsMatchRecipient, appointmentState],
  );

  // True while the body has a data-bound table and the recipient's REAL rows
  // aren't available yet (still in flight, or only stale rows from the previous
  // recipient on hand). Expanding now would paint SAMPLE rows (Anna/Lukas/…) —
  // or the wrong recipient's rows — that get swapped a tick later, a visible
  // flash. So we hold the preview (keep prior HTML, or blank on first paint)
  // until the matching records arrive and re-trigger this effect.
  const awaitingCompanions =
    hasDataTable &&
    !recordsMatchRecipient &&
    companionErrorId !== recipientGuestId;
  const awaitingAppointments =
    hasAppointmentTable &&
    !appointmentsMatchRecipient &&
    appointmentErrorId !== recipientGuestId;
  const awaitingRealRecords =
    !!recipientGuestId &&
    !!resolvedEventId &&
    (awaitingCompanions || awaitingAppointments);

  // The recipient's REAL rows failed to load, so the table falls back to
  // illustrative SAMPLE rows (Anna/Lukas/…). Surface a badge so the admin
  // doesn't mistake the placeholder rows for the recipient's actual data.
  const showsSampleData =
    (hasDataTable && companionErrorId === recipientGuestId) ||
    (hasAppointmentTable && appointmentErrorId === recipientGuestId);

  // QR markers resolve via an async backend token fetch, so the preview
  // HTML is computed in an effect (not a sync useMemo). A cancellation
  // flag drops stale results when the guest / language changes mid-flight.
  useEffect(() => {
    // Don't flash SAMPLE table rows while the recipient's real companions
    // load — keep the prior HTML until they arrive (the fetch re-runs this
    // effect by flipping `awaitingRealRecords`/`realRecords`).
    if (awaitingRealRecords) return;
    let cancelled = false;
    const rawHtml = resolveLangValue(
      data.html,
      effLang,
      data.primaryLanguage,
      "",
    );

    // Honor per-block display conditions: strip the `VLT-IF` markers for this
    // recipient FIRST (same order the consumer uses at real send), so the
    // preview matches the inbox instead of showing every conditioned block.
    // No guest selected → leave markers in place so nothing is hidden blindly;
    // they're invisible HTML comments either way.
    const conditioned = guest
      ? expandConditionalMarkers(
          rawHtml,
          guest as unknown as Record<string, unknown>,
        )
      : rawHtml;

    // Expand the opaque `{{dataTable:…}}` marker into a styled preview table the
    // same way the consumer does at real send. Data-bound tables stand in
    // believable SAMPLE rows (no live payload client-side); custom tables render
    // their inline rows. Without this the raw token would show up verbatim.
    // effLang (the body variant's language), NOT the guest's raw language —
    // the consumer formats sample-equivalent rows with the render language,
    // so a language-less guest on a German template previews German dates.
    const baseHtml = expandDataTableMarkers(
      conditioned,
      guest as unknown as Record<string, unknown> | null,
      realRecords,
      effLang,
    );

    let forbidden = false;
    resolveQrPlaceholdersForPreview(baseHtml, {
      guest,
      eventId: resolvedEventId,
      // What the block renders when no real token can be minted — the same
      // mock the canvas draws, so the preview and the editor agree.
      mockGuestQrValue: QR_GUEST_SAMPLE_VALUE,
      onGuestTokenForbidden: () => {
        forbidden = true;
      },
    })
      .then((withQr) => {
        if (cancelled) return;
        setQrPerRecipient(forbidden);
        setPreviewHtml(guest ? replaceVariablesInHtml(withQr, guest, previewCtx) : withQr);
      })
      .catch(() => {
        if (cancelled) return;
        setQrPerRecipient(forbidden);
        // Fall back to the un-QR'd HTML rather than a blank preview.
        setPreviewHtml(guest ? replaceVariablesInHtml(baseHtml, guest, previewCtx) : baseHtml);
      });

    return () => {
      cancelled = true;
    };
  }, [
    data.html,
    data.primaryLanguage,
    guest,
    resolvedEventId,
    realRecords,
    awaitingRealRecords,
    effLang,
    previewCtx,
  ]);


  return (
    <div
      ref={containerRef}
      className="w-full shadow-lg"
      style={{
        backgroundColor: data.contentAreaBackgroundColor,
      }}
    >
      {showsSampleData && (
        <div className="flex items-center gap-2 bg-amber-50 text-amber-800 border-b border-amber-200 px-3 py-2 caption">
          <IconAlertTriangle size={16} className="shrink-0" />
          <span>{t("sampleDataNotice")}</span>
        </div>
      )}
      {qrPerRecipient && (
        <div
          data-testid="qr-per-recipient-badge"
          role="status"
          className="flex items-center gap-2 surface-secondary font-tertiary border-b outline-quaternary px-3 py-1.5 caption"
        >
          <IconQrcode size={14} className="shrink-0" />
          <span>{tSend("qrGeneratedPerRecipient")}</span>
        </div>
      )}
      <div
        style={{
          height: `${(iframeHeight || 800) * scale}px`,
          // `transform: scale()` shrinks what you SEE, not what the box
          // occupies: the iframe keeps its full layout width (>= 640px) and
          // hands that width to every ancestor. The height above is already corrected for
          // the scale; the width was not, so a 600px box sat inside a 356px
          // dialog and only stayed out of sight because one ancestor happened
          // to carry `overflow-x: hidden`. Clipping it at its own boundary
          // makes the scaled preview honest about its size instead.
          width: "100%",
          overflow: "hidden",
        }}
      >
        <iframe
          ref={iframeRef}
          title={t("emailPreview")}
          sandbox="allow-same-origin"
          style={{
            width: `${iframeWidth}px`,
            height: `${iframeHeight || 800}px`,
            border: "none",
            transformOrigin: "top left",
            transform: `scale(${scale})`,
            pointerEvents: "none",
          }}
        />
      </div>
    </div>
  );
};
